import type { DashboardData } from '@pointfive/contracts';
/** The design reference's own P&L calendar, reproduced exactly — and reproduced HERE rather than in the screen.
 *
 *  The view-model used to run this generator itself, which meant the calendar was decoration presented as a
 *  trader's money: seeded from the month alone, identical for every account, and shown in full to accounts that had
 *  never traded a day. Moving it into the dataset changes nothing about what the sim binding renders — the
 *  arithmetic below is the reference's own, so the visual baselines still match — while freeing the screen to read
 *  the live binding's real `day` aggregate instead of inventing one.
 *
 *  Jan–Jul 2026 is the reference's window; July is partial at the 24th, its "today". */
function referenceCalendar(): Record<string, number> {
  const out: Record<string, number> = {};
  const YEAR = 2026;
  for (let month = 0; month <= 6; month++) {
    const days = new Date(YEAR, month + 1, 0).getDate();
    const first = (new Date(YEAR, month, 1).getDay() + 6) % 7; // Monday-first, as the grid is laid out
    const today = month === 6 ? 24 : 0;
    let seed = ((YEAR * 12 + month) * 7919 + 104729) % 233280;
    const rnd = () => (seed = (seed * 9301 + 49297) % 233280) / 233280;
    for (let d = 1; d <= days; d++) {
      const dow = (first + d - 1) % 7;
      const live = dow < 5 && (!today || d <= today) && rnd() > 0.1;
      const value = live ? Math.round(((rnd() - 0.34) * 4200) / 20) * 20 : 0;
      // A day with no trading is ABSENT from the map rather than zero: the calendar draws "no trading" differently
      // from "flat", and a zero would claim the trader was at the desk and broke even.
      if (value !== 0)
        out[`${YEAR}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`] = value;
    }
  }
  return out;
}

/** Dashboard slice of the deterministic demo dataset — SETS, G, ALL, SESS, HOUR_W/HOUR_L transcribed verbatim from the design
 *  reference (script lines 4148–4173, 4459–4465, 4537–4544, 4693–4700). simulated: seeded demo inputs, not a broker feed. */
export const dashboardData: DashboardData = {
  equity: 542692,
  eqSets: {
    day: [
      ['8:12', 1920, 0.36],
      ['8:40', 4278, 0.79],
      ['8:44', -640, -0.12],
      ['9:02', 4480, 0.83],
      ['9:33', 840, 0.16],
      ['10:15', -320, -0.06],
      ['11:04', 2892, 0.53],
      ['13:20', 1974, 0.36],
      ['14:08', -410, -0.08],
      ['15:41', 2052, 0.38],
    ],
    week: [
      ['Mon', 6420, 1.21],
      ['Tue', -1180, -0.22],
      ['Wed', 9240, 1.72],
      ['Thu', 3860, 0.71],
      ['Fri', 15676, 2.97],
      ['Sat', 0, 0],
      ['Sun', 0, 0],
    ],
    month: [
      ['1', 2140, 0.42],
      ['2', -820, -0.16],
      ['3', 3960, 0.77],
      ['4', 1240, 0.24],
      ['5', -1460, -0.28],
      ['6', 0, 0],
      ['7', 0, 0],
      ['8', 5280, 1.02],
      ['9', 2870, 0.55],
      ['10', -640, -0.12],
      ['11', 4120, 0.79],
      ['12', 1890, 0.36],
      ['13', 0, 0],
      ['14', 0, 0],
      ['15', -2210, -0.42],
      ['16', 6340, 1.21],
      ['17', 3480, 0.66],
      ['18', 920, 0.18],
      ['19', -1130, -0.22],
      ['20', 0, 0],
      ['21', 0, 0],
      ['22', 4780, 0.91],
      ['23', 12406, 2.36],
      ['24', 15676, 2.97],
    ],
    year: [
      ['Jan', 8420, 1.68],
      ['Feb', -3240, -0.64],
      ['Mar', 12680, 2.48],
      ['Apr', 6140, 1.19],
      ['May', -1820, -0.35],
      ['Jun', 9460, 1.82],
      ['Jul', 42692, 8.54],
      ['Aug', 0, 0],
      ['Sep', 0, 0],
      ['Oct', 0, 0],
      ['Nov', 0, 0],
      ['Dec', 0, 0],
    ],
    all: [
      ['Q3 24', 14200, 3.12],
      ['Q4 24', -6400, -1.38],
      ['Q1 25', 22840, 4.86],
      ['Q2 25', 18260, 3.71],
      ['Q3 25', -4120, -0.82],
      ['Q4 25', 31480, 6.14],
      ['Q1 26', 26940, 5.02],
      ['Q2 26', 19640, 3.58],
      ['Q3 26', 42692, 8.54],
    ],
  },
  gauges: {
    day: {
      label: 'today',
      closed: 9,
      wins: 6,
      open: 2,
      daysTraded: 1,
      daysProfit: 1,
      roi: 0.42,
      pnl: 2120,
      scale: 2,
    },
    week: {
      label: 'this week',
      closed: 24,
      wins: 15,
      open: 2,
      daysTraded: 5,
      daysProfit: 4,
      roi: 1.87,
      pnl: 9350,
      scale: 5,
    },
    month: {
      label: 'this month',
      closed: 86,
      wins: 51,
      open: 2,
      daysTraded: 18,
      daysProfit: 12,
      roi: 8.54,
      pnl: 42692,
      scale: 15,
    },
    year: {
      label: '2026',
      closed: 642,
      wins: 379,
      open: 2,
      daysTraded: 148,
      daysProfit: 94,
      roi: 26.31,
      pnl: 131550,
      scale: 40,
    },
    all: {
      label: 'since Q4 2023',
      closed: 2184,
      wins: 1279,
      open: 2,
      daysTraded: 512,
      daysProfit: 321,
      roi: 118.42,
      pnl: 592100,
      scale: 150,
    },
  },
  accounts: [
    { id: 'a1', name: 'Alpha 100K', col: 'var(--sky-fill)', start: 104000, drift: 1.6, vol: 1.5, seed: 7 },
    { id: 'a2', name: 'Prop Scale II', col: 'var(--pnl-p1)', start: 186000, drift: 1.1, vol: 2.4, seed: 19 },
    {
      id: 'a3',
      name: 'Personal Live',
      col: 'var(--neg-fill)',
      start: 242000,
      drift: 0.8,
      vol: 0.9,
      seed: 31,
    },
    { id: 'a4', name: 'Swing Fund', col: 'var(--info)', start: 68000, drift: 2.1, vol: 2.0, seed: 53 },
    {
      id: 'a5',
      name: 'Crypto Hedge',
      col: 'var(--slate-fill)',
      start: 128000,
      drift: 1.4,
      vol: 3.1,
      seed: 71,
    },
    { id: 'a6', name: 'Index Carry', col: 'var(--vio-fill)', drift: 0.6, start: 96000, vol: 1.2, seed: 97 },
  ],
  sessions: [
    ['Sydney', 0.07, 46],
    ['Asia', 0.16, 52],
    ['London', 0.41, 63],
    ['New York', 0.36, 59],
  ],
  hourWeights: [0.06, 0.13, 0.16, 0.11, 0.05, 0.04, 0.09, 0.13, 0.1, 0.06, 0.04, 0.03],
  hourLabels: ['08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19'],
  calendar: referenceCalendar(),
};
